import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { updateUserExamBE } from '../services/users';
import { fetchExams } from '../services/exams';
import { DailyDueMode, DailyNewMode } from '../types/User';

interface UseUserExamUpdateOptions {
  showSuccessMessage?: boolean;
  clearSession?: boolean;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  // When true, refetches exams after a primary exam change. Disable in onboarding for speed.
  refetchOnPrimaryChange?: boolean;
}

interface UseUserExamUpdateReturn {
  updateExam: (params: UpdateUserExamParams) => Promise<void>;
  isUpdating: boolean;
}

// Type for updateUserExamBE parameters
interface UpdateUserExamParams {
  userId: string;
  examId: string;
  name?: string | null;
  examDate?: string | null;
  primary?: boolean;
  selectedFlashcardCount?: number;
  selectedVignetteCount?: number;
  isUsingFlashcards?: boolean;
  isUsingQbank?: boolean;
  isUsingVideos?: boolean;
  hasCompletedRealExam?: boolean;
  realExamScore?: number | null;
  ignoredExamDate?: boolean;
  clearTodaySession?: boolean;
  lectureMode?: 'AUTOPILOT' | 'MANUAL';
  dailyFlashcardsNewMode?: DailyNewMode;
  dailyFlashcardsNewFixedCount?: number;
  dailyFlashcardsDueMode?: DailyDueMode;
  dailyFlashcardsDueFixedCap?: number;
  dailyQbankNewMode?: DailyNewMode;
  dailyQbankNewFixedCount?: number;
  dailyQbankDueMode?: DailyDueMode;
  dailyQbankDueFixedCap?: number;
  dailyVideosNewMode?: DailyNewMode;
  dailyVideosNewFixedCount?: number;
  applyDailySessionSizeToAllStandardExams?: boolean;
}

export const useUserExamUpdate = (
  refreshStudySessionAndContent?: () => void,
  options: UseUserExamUpdateOptions = {}
): UseUserExamUpdateReturn => {
  const {
    showSuccessMessage = true,
    clearSession = false,
    onSuccess,
    onError,
    refetchOnPrimaryChange = true,
  } = options;

  const [isUpdating, setIsUpdating] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const updateExam = async (params: UpdateUserExamParams) => {
    try {
      setIsUpdating(true);

      // Update the exam with provided parameters
      await updateUserExamBE({
        ...params,
        clearTodaySession: clearSession || params.clearTodaySession,
      });

      // We only need to refetch the exams if the primary exam potentially changed
      if (params.primary && refetchOnPrimaryChange) {
        await fetchExams();
      }

      if (refreshStudySessionAndContent) {
        await refreshStudySessionAndContent();
      }

      // Show success message if enabled
      if (showSuccessMessage) {
        enqueueSnackbar('Exam updated.', {
          autoHideDuration: 3000,
          style: { width: 'fit-content' },
        });
      }

      // Call success callback if provided
      onSuccess?.();
    } catch (error) {
      console.error('Error updating exam:', error);
      if (onError) {
        onError(error);
      } else {
        // The service rethrows now; a caller with no handler of its own must
        // not turn that into silence (it used to be a false "Exam updated.").
        enqueueSnackbar('Unable to update exam. Please try again.', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return { updateExam, isUpdating };
};
